// Özet sayfası — /clients/kpi/ozet (M16 T14, mockup 01/17; D5 durum şeridi, kural 2/13/14).
// Şerit sayaçları countSetFillStatus'tan (tek kaynak); tıklanan kalem sekmeyi filtreler. Stat
// kartları + sekmeli karşılaştırma tablosu (Tümü / Eksik / Kontrol bekleyen / Onay bekleyen).
// Satıra tıkla → Veri Girişi'nde ?highlight=<code>. AI Kontrolü raili T15'te. Kırılım (spec §6.6):
// satırlar "gösterge" tanımıyla (grup/kategori değil), DR5 sayaç adları `KpiCounterStrip`'ten;
// türetilmiş toplam "veri bekleyen"e girmez.

import { useEffect, useMemo, useState } from "react";
import type { KpiTargetWithProgress } from "@mahir/shared";
import { useNavigate } from "react-router-dom";
import { setKpiIndicatorChecked } from "../../api/kpi";
import { listKpiTargets } from "../../api/kpi-targets";
import { pickPrimaryTarget } from "../../lib/kpi-targets-ui";
import { KpiApprovalQueue } from "../../components/kpi/KpiApprovalQueue";
import {
  KpiComparisonTable,
  rowMatchesTab,
  type KpiComparisonRow,
  type OzetTab,
} from "../../components/kpi/KpiComparisonTable";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { KpiCounterStrip, KpiStatCards } from "../../components/kpi/KpiStatCards";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { isBreakdownDerivedTotal, isMetricIndicator } from "../../lib/kpi-grid-breakdown";
import { subtreeIndicators } from "../../lib/kpi-tree-list";
import { AiCheckPanel } from "../../components/kpi/AiCheckPanel";
import { KpiFormMismatchList } from "../../components/kpi/KpiFormMismatchList";
import { KpiPendingNotes } from "../../components/kpi/KpiPendingNotes";
import { useKpiNoteSummary } from "../../hooks/use-kpi-note-summary";
import { LoadingState } from "../../components/common/StateSurface";
import { KPI_COPY } from "../../lib/kpi-ui";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { useCreateSetNav } from "./use-create-set-nav";

const btn: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.82rem",
  color: "var(--color-text)",
  minHeight: 34,
};

export function KpiOzetPage(): JSX.Element {
  const createSet = useCreateSetNav();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCheck = user?.permissions["kpi.edit"] === true;
  const canVerify = user?.permissions["kpi.verify"] === true;
  const grid = useKpiStore((s) => s.grid);
  const setId = useKpiStore((s) => s.setId);
  const fill = useKpiStore((s) => s.fill);
  const years = useKpiStore((s) => s.years);
  const [tab, setTab] = useState<OzetTab>("all");
  // Dalga 3 — hücre notu özeti: onay kuyruğu satırı + "Yanıt bekleyen notlar (n)" kartı (tek çağrı)
  const noteSummary = useKpiNoteSummary("internal", setId, years, grid);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);
  // TODO-KPI-01 — hedefler ayrı sorgu (resolve'a dokunmaz); hedef yoksa kolonlar render edilmez
  const [targets, setTargets] = useState<Map<string, KpiTargetWithProgress>>(() => new Map());
  useEffect(() => {
    let alive = true;
    if (!setId) {
      setTargets(new Map());
      return;
    }
    void listKpiTargets(setId)
      .then((list) => {
        if (alive) setTargets(pickPrimaryTarget(list));
      })
      .catch(() => {
        if (alive) setTargets(new Map());
      });
    return () => {
      alive = false;
    };
  }, [setId, grid]);

  // Dönem = karşılaştırma yılı; Yıllar aralığı değişip seçili yıl düşerse aralığın son yılına döner
  const curYear =
    year !== null && years.includes(year)
      ? year
      : (years[years.length - 1] ?? new Date().getFullYear());
  const prevYear = years.includes(curYear - 1) ? curYear - 1 : null;
  const categories = useMemo(
    () =>
      grid
        ? grid.indicators.filter(
            (i) => i.parentId === null && grid.indicators.some((c) => c.parentId === i.id),
          )
        : [],
    [grid],
  );

  const rows = useMemo<KpiComparisonRow[]>(() => {
    if (!grid) return [];
    const leaf = (i: (typeof grid.indicators)[number]): boolean =>
      isMetricIndicator(
        i,
        grid.indicators.filter((c) => c.parentId === i.id),
      );
    // atası kategori olanlar — dizi sırasından bağımsız ata yürüyüşü (çocuk üstünden önce gelebilir)
    const inSub = new Set(subtreeIndicators(grid.indicators, categoryId).map((i) => i.id));
    const inCategory = (ind: { id: string }): boolean =>
      !categoryId || (ind.id !== categoryId && inSub.has(ind.id));
    return grid.indicators
      .filter((i) => leaf(i) && inCategory(i))
      .map((indicator) => ({
        indicator,
        cur: indicator.consolidated?.[curYear],
        prev: prevYear ? indicator.consolidated?.[prevYear] : undefined,
        ...(isBreakdownDerivedTotal(indicator, grid.indicators) ? { derivedTotal: true } : {}),
      }));
  }, [grid, categoryId, curYear, prevYear]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      missing: rows.filter((r) => rowMatchesTab(r, "missing")).length,
      check: rows.filter((r) => rowMatchesTab(r, "check")).length,
      approval: fill?.awaitingVerification ?? 0,
      variants: grid ? grid.indicators.filter((i) => i.pendingFormulaId).length : 0,
      calcError: rows.filter((r) => rowMatchesTab(r, "calcError")).length,
    }),
    [rows, fill, grid],
  );
  const visible = rows.filter((r) => rowMatchesTab(r, tab));
  // T7 — kategori ağacı: kategori süzgeci açıksa yalnız o alt ağacın göstergeleri bant kaynağı olur
  const treeIndicators = useMemo(() => {
    if (!grid) return [];
    return subtreeIndicators(grid.indicators, categoryId);
  }, [grid, categoryId]);
  // B-17 (FE): AI uyarılarında kod yerine etiket + işletme adı (kod `title`'da kalır)
  const labelByCode = useMemo(
    () => Object.fromEntries((grid?.indicators ?? []).map((i) => [i.code, i.label])),
    [grid],
  );
  const entityNameById = useMemo(
    () => Object.fromEntries((grid?.entities ?? []).map((e) => [e.id, e.name])),
    [grid],
  );
  const singleEntity =
    (grid?.entities.filter((e) => e.isActive && !grid.entities.some((c) => c.parentId === e.id))
      .length ?? 0) <= 1;

  const tabBtn = (key: OzetTab, label: string, n: number): JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className="px-3 py-2 text-sm"
      style={{
        fontWeight: 600,
        color: tab === key ? "var(--color-primary)" : "var(--color-text-2)",
        borderBottom: `2px solid ${tab === key ? "var(--color-primary)" : "transparent"}`,
        marginBottom: -1,
      }}
    >
      {label} ({n})
    </button>
  );

  return (
    <KpiScreen
      title={KPI_COPY.titles.ozet}
      showYears
      onCreateSet={() => createSet()}
      onCreateSetForClient={(id) => createSet(id)}
      right={
        <div className="flex items-center gap-2">
          <label
            className="text-xs flex items-center gap-1"
            style={{ color: "var(--color-text-2)" }}
          >
            {KPI_COPY.ozet.periodLabel}
            <select
              aria-label={KPI_COPY.ozet.periodLabel}
              style={btn}
              value={curYear}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {[...years].reverse().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span
              data-testid="kpi-ozet-prev-note"
              style={{ color: prevYear ? "var(--color-text-3)" : "var(--color-warning-text)" }}
            >
              {prevYear
                ? KPI_COPY.ozetCards.periodCompare(prevYear)
                : KPI_COPY.ozetCards.periodNoPrev(curYear - 1)}
            </span>
          </label>
          <select
            aria-label={KPI_COPY.grid.categoryLabel}
            style={btn}
            value={categoryId ?? ""}
            onChange={(e) => setCategoryId(e.target.value || null)}
          >
            <option value="">{KPI_COPY.grid.categoryAll}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {!grid || !fill ? (
        // güvenlik/UX taraması 2026-10-08 (RK36): metinler KPI_COPY.ozet'ten (e2e 15 mirror)
        <LoadingState label={KPI_COPY.ozet.loading} />
      ) : (
        // P2-6: 1280 altında rail daralır, 1100 altında alt alta (globals.css `.kpi-ozet-grid`)
        <div className="kpi-ozet-grid">
          <div className="min-w-0">
            {/* D5 — durum şeridi (tıklanan kalem sekmeyi filtreler) */}
            <div
              className="flex items-center gap-4 flex-wrap text-sm mb-4 px-3 py-2 rounded-xl"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
              }}
              aria-label={KPI_COPY.ozet.stripLabel}
            >
              {/* DR5 — dört sayaç adı (her biri kendi süzgecini açar) */}
              <KpiCounterStrip
                counts={{
                  indicators: counts.all,
                  missing: counts.missing,
                  approval: counts.approval,
                  calcError: counts.calcError,
                }}
                onFilter={setTab}
              />
              <button
                type="button"
                onClick={() => setTab("check")}
                style={{ color: "var(--color-warning-text)" }}
              >
                <b>{counts.check}</b> kontrol bekliyor
              </button>
              <span style={{ marginLeft: "auto", color: "var(--color-success)" }}>
                ✓ {rows.filter((r) => r.indicator.checkedAt).length} kontrol edildi
              </span>
            </div>

            <KpiStatCards grid={grid} year={curYear} prevYear={prevYear} />

            <div
              role="tablist"
              className="flex gap-1 mb-0"
              style={{ borderBottom: "1px solid var(--color-border)" }}
            >
              {tabBtn("all", KPI_COPY.ozet.tabs.all, counts.all)}
              {tabBtn("missing", KPI_COPY.ozet.tabs.missing, counts.missing)}
              {tabBtn("check", KPI_COPY.ozet.tabs.check, counts.check)}
              {tabBtn(
                "approval",
                counts.variants > 0
                  ? KPI_COPY.ozet.tabs.approvalWithVariants(counts.variants)
                  : KPI_COPY.ozet.tabs.approval,
                counts.approval,
              )}
              {/* DR5 "Hesaplanamayan toplam" süzgeci seçili durumu diğer sekmeler gibi gösterir */}
              {counts.calcError > 0 || tab === "calcError"
                ? tabBtn("calcError", KPI_BREAKDOWN_COPY.counters.calcErrorTab, counts.calcError)
                : null}
            </div>
            <div
              className="rounded-b-xl"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderTop: 0,
              }}
            >
              {tab === "approval" ? (
                <KpiApprovalQueue
                  grid={grid}
                  canVerify={canVerify}
                  singleEntity={singleEntity}
                  notes={noteSummary}
                  years={years}
                  onExtendYears={(ys) =>
                    void useKpiStore
                      .getState()
                      .setYears(Array.from(new Set([...years, ...ys])).sort((a, b) => a - b))
                  }
                  onChanged={() => void useKpiStore.getState().refreshGrid()}
                />
              ) : (
                <KpiComparisonTable
                  rows={visible}
                  tree={{
                    indicators: treeIndicators,
                    allRows: rows,
                    filtered: tab !== "all",
                    screen: "ozet",
                  }}
                  year={curYear}
                  prevYear={prevYear}
                  canCheck={canCheck}
                  targets={targets}
                  onOpen={(code) =>
                    void navigate(`/clients/kpi/tablo?highlight=${encodeURIComponent(code)}`)
                  }
                  onToggleChecked={(id, checked) => {
                    void setKpiIndicatorChecked(id, checked).then(() =>
                      useKpiStore.getState().refreshGrid(),
                    );
                  }}
                />
              )}
              <div
                className="flex justify-between text-xs px-3 py-2"
                style={{ color: "var(--color-text-3)", borderTop: "1px solid var(--color-border)" }}
              >
                <span>
                  <b style={{ color: "var(--color-primary)" }}>
                    {tab === "approval" ? counts.approval : visible.length}
                  </b>{" "}
                  / {counts.all}
                </span>
                <span>
                  Satıra tıkla → Veri Girişi'nde o göstergeye git · ✓ kontrol = gözden geçirildi
                  (onaydan bağımsız)
                </span>
              </div>
            </div>
          </div>
          {/* T15/T21 — sağ rail: AI kontrolü + form ↔ KPI uyuşmazlıkları */}
          <aside className="flex flex-col gap-3" aria-label="Kontroller">
            <AiCheckPanel
              setId={setId}
              canRun={canCheck}
              years={years}
              labelByCode={labelByCode}
              entityNameById={entityNameById}
              onOpenIndicator={(code) =>
                void navigate(`/clients/kpi/tablo?highlight=${encodeURIComponent(code)}`)
              }
            />
            <KpiPendingNotes grid={grid} cells={noteSummary.cells} reload={noteSummary.reload} />
            <KpiFormMismatchList
              setId={setId}
              canEdit={canCheck}
              onChanged={() => void useKpiStore.getState().refreshGrid()}
            />
          </aside>
        </div>
      )}
    </KpiScreen>
  );
}
